import {
  DocumentCategoryItem,
  ROUTE_LOOKUP,
  SelectOption,
  SplitDisplayFileItem,
  SplitListsOutput,
  SplitRowItem,
} from "us.collection.input-output/constants";

/**
 * Formats a number of bytes into a human-readable string (e.g. "1.23 MB").
 * @param bytes Number of bytes to format
 * @returns Formatted size string, or "-" when `bytes` is less than or equal to 0
 */
export const formatBytes = (bytes: number): string => {
  try {
    if (!bytes || bytes <= 0) return "-";

    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizes.length - 1,
    );

    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  } catch (error) {
    console.error("Error formatting bytes:", error);
    return "-";
  }
};

/**
 * Builds a route path for a given entity type and assignTo identifier.
 * @param entityType The entity type (e.g., "case", "subcase", "ar", "creditor")
 * @param assignTo The identifier value to insert into the route
 * @returns Route path string (e.g. `/case/123`) or empty string when `assignTo` is falsy
 */
export const getRoutePath = (entityType: string, assignTo: string): string => {
  const assignValue = String(assignTo || "").trim();
  if (!assignValue) return "";

  try {
    const normalized = String(entityType || "")
      .trim()
      .toLowerCase();
    const routePrefix = ROUTE_LOOKUP[normalized];
    return routePrefix ? `/${routePrefix}/${assignValue}` : "";
  } catch (error) {
    console.error("Error building route path:", error);
    return "";
  }
};

/**
 * Creates a new `File` object with an updated filename. If `documentName` has no
 * extension, the original file's extension is preserved.
 * @param file Original `File` object
 * @param documentName Desired document name (may include extension)
 * @returns New `File` instance with the updated name and preserved metadata
 */
export const createRenamedFile = (file: File, documentName: string): File => {
  if (!file)
    throw new Error("createRenamedFile: Source file payload is required");
  if (!documentName || !documentName.trim()) return file;

  try {
    const cleanedDocName = documentName.trim();

    const dotIndex = file.name.lastIndexOf(".");
    const originalExt = dotIndex !== -1 ? file.name.slice(dotIndex + 1) : "";

    const hasTargetExtension = /\.[a-zA-Z0-9]{1,5}$/.test(cleanedDocName);

    let finalName = cleanedDocName;
    if (!hasTargetExtension && originalExt) {
      finalName = `${cleanedDocName}.${originalExt}`;
    }

    return new File([file], finalName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.error("Error creating renamed file:", error);
    return file;
  }
};

/**
 * Maps an array of document category objects to unique option objects for selects.
 * @param documentCategories Array of document category objects
 * @returns Array of unique option objects shaped like `{ value: string, label: string }`
 */
export const getCategoryOptions = (
  documentCategories: DocumentCategoryItem[],
): SelectOption[] => {
  if (!documentCategories || documentCategories.length === 0) return [];

  const seenCategoriesSet = new Set<string>();
  const options: SelectOption[] = [];

  for (let i = 0; i < documentCategories.length; i++) {
    const categoryName = documentCategories[i]?.documentCategory;

    if (!categoryName) continue;

    if (!seenCategoriesSet.has(categoryName)) {
      seenCategoriesSet.add(categoryName);
      options.push({
        value: categoryName,
        label: categoryName,
      });
    }
  }

  return options;
};

/**
 * Builds two lists used by the split-upload UI: `rows` (editable metadata) and `fileList` (display data).
 * @param files Array of `File` objects to convert
 * @returns Object containing `rows` and `fileList` arrays
 */
export const makeSplitLists = (files: File[]): SplitListsOutput => {
  if (!files || files.length === 0) return { rows: [], fileList: [] };

  try {
    const rows: SplitRowItem[] = [];
    const fileList: SplitDisplayFileItem[] = [];

    for (let idx = 0; idx < files.length; idx++) {
      const file = files[idx];

      const stableKey = `split-${file.name}-${file.size}-${file.lastModified || idx}`;

      rows.push({
        key: stableKey,
        documentName: file.name || `page-${idx}`,
        assignTo: "",
        category: "",
        debtorName: "",
        creditorName: "",
        checked: false,
        file,
      });

      fileList.push({
        key: stableKey,
        documentName: file.name || "",
        assignTo: "",
        creditorName: "",
        debtorName: "",
        file,
        pages: "",
        size: formatBytes(file.size || 0),
      });
    }

    return { rows, fileList };
  } catch (error) {
    console.error("Error making split lists:", error);
    return { rows: [], fileList: [] };
  }
};
